import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { formatDateLt, LT_MONTHS } from "./date";

const VALID_UNTIL_PRODUCT_CODES = new Set(["H9", "H10", "HBB"]);
const VALIDITY_YEARS_BY_PRODUCT_CODE: Record<string, number> = {
  H9: 2,
  H10: 5,
  HBB: 5,
};

interface GenerateCertificatePdfParams {
  templatePath?: "/certificate.pdf" | "/certificatehb.pdf";
  name: string;
  surname: string;
  birthDate: string;
  productCode: string;
  date: string;
  countNumber: string;
  hours: string;
  fieldCode: string;
}

const parseCertificateDate = (value: string) => {
  const trimmedValue = value.trim();

  const isoMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  }

  const ltMatch = trimmedValue.match(/^(\d{4})\s*m\.\s+([a-ząčęėįšųūž]+)\s+(\d{1,2})\s*d\.$/i);
  if (!ltMatch) {
    return null;
  }

  const [, year, monthName, day] = ltMatch;
  const monthIndex = LT_MONTHS.findIndex(
    (month) => month.toLowerCase() === monthName.toLowerCase()
  );

  if (monthIndex === -1) {
    return null;
  }

  return new Date(Date.UTC(Number(year), monthIndex, Number(day)));
};

const getCertificateValidUntil = (dateValue: string, productCode: string) => {
  const normalizedProductCode = productCode.trim().toUpperCase();
  if (!VALID_UNTIL_PRODUCT_CODES.has(normalizedProductCode)) {
    return null;
  }

  const certificateDate = parseCertificateDate(dateValue);
  if (!certificateDate || Number.isNaN(certificateDate.getTime())) {
    return null;
  }

  const validityYears = VALIDITY_YEARS_BY_PRODUCT_CODE[normalizedProductCode] ?? 5;
  certificateDate.setUTCFullYear(certificateDate.getUTCFullYear() + validityYears);
  return `Galioja iki ${formatDateLt(
    new Date(certificateDate.getUTCFullYear(), certificateDate.getUTCMonth(), certificateDate.getUTCDate())
  )}`;
};

export const generateCertificatePdf = async ({
  templatePath = "/certificate.pdf",
  name,
  surname,
  birthDate,
  productCode,
  date,
  countNumber,
  hours,
  fieldCode,
}: GenerateCertificatePdfParams) => {
  const existingPdfBytes = await fetch(templatePath).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  pdfDoc.registerFontkit(fontkit);

  const fontBytes = await fetch("/fonts/Roboto.ttf").then((res) => res.arrayBuffer());
  const font = await pdfDoc.embedFont(fontBytes);

  const pages = pdfDoc.getPages();
  const firstPage = pages[0];
  const validUntilLabel = getCertificateValidUntil(date, productCode);

  let x = 66;
  const y = 405;
  const gap = 10;

  firstPage.drawText(name, {
    x,
    y,
    size: 16,
    font,
    color: rgb(0, 0, 0),
  });
  x += font.widthOfTextAtSize(name, 16) + gap;

  firstPage.drawText(surname, {
    x,
    y,
    size: 16,
    font,
    color: rgb(0, 0, 0),
  });

  const birthDateWidth = font.widthOfTextAtSize(birthDate, 16);
  const rightEdgeX = 530;

  firstPage.drawText(birthDate, {
    x: rightEdgeX - birthDateWidth,
    y: 405,
    size: 16,
    font,
    color: rgb(0, 0, 0),
  });

  firstPage.drawText(productCode, {
    x: 392,
    y: 220,
    size: 14,
    font,
    color: rgb(0, 0, 0),
  });

  const rightEdgeDateX = 320;
  const dateWidth = font.widthOfTextAtSize(date, 16);

  firstPage.drawText(date, {
    x: rightEdgeDateX - dateWidth,
    y: 460,
    size: 16,
    font,
    color: rgb(0, 0, 0),
  });

  const gapDate = 4;
  const nrText = "Nr.";
  const nrTextWidth = font.widthOfTextAtSize(nrText, 16);
  firstPage.drawText(nrText, {
    x: rightEdgeDateX + gapDate,
    y: 460,
    size: 16,
    font,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(" " + countNumber, {
    x: rightEdgeDateX + gapDate + nrTextWidth,
    y: 460,
    size: 16,
    font,
    color: rgb(0, 0, 0),
  });

  firstPage.drawText(hours, {
    x: 205,
    y: 239,
    size: 14,
    font,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(fieldCode, {
    x: 280,
    y: 355,
    size: 16,
    font,
    color: rgb(0, 0, 0),
  });

  if (validUntilLabel) {
    firstPage.drawText(validUntilLabel, {
      x: 190,
      y: 199,
      size: 14,
      font,
      color: rgb(0, 0, 0),
    });
  }

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes as any], { type: "application/pdf" });
};
